import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    BookOpen,
    CheckCircle2,
    CheckSquare,
    Clock,
    Coffee,
    Map,
    Square,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { Reveal } from "../lib/motion";
import { findTopic, isDrafted, neighbours } from "../data/topics";

const VIEWS = [
    { id: "read", label: "Read it", icon: BookOpen, testid: "view-tab-read" },
    { id: "see", label: "See it", icon: Map, testid: "view-tab-see" },
    { id: "do", label: "Do it", icon: CheckSquare, testid: "view-tab-do" },
];

export default function TopicDetail() {
    const { slug } = useParams();
    const topic = findTopic(slug);
    const [view, setView] = useState("read");
    if (!topic) {
        return <Navigate to="/where-should-i-start" replace />;
    }

    const drafted = isDrafted(topic);
    const { prev, next } = neighbours(slug);

    return (
        <div
            className="min-h-screen bg-page text-ink"
            data-testid="topic-detail-page"
        >
            <Nav />
            <main>
                {/* Hero */}
                <section className="relative overflow-hidden border-b border-ink/10 py-20 lg:py-28">
                    <div className="pointer-events-none absolute -top-16 -right-16 h-[480px] w-[480px] animate-blob rounded-full bg-coral/15 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-24 left-1/3 h-[360px] w-[360px] animate-blob rounded-full bg-sage/25 blur-3xl" />

                    <div className="relative mx-auto max-w-[1100px] px-5 lg:px-10">
                        <Link
                            to="/where-should-i-start"
                            className="mb-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="topic-detail-back"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            All playbook topics
                        </Link>

                        <Reveal>
                            <div className="mb-6 flex flex-wrap items-center gap-4 text-[11px] font-bold uppercase tracking-[0.3em]">
                                <span
                                    className="border-2 border-coral bg-coral px-3 py-1 font-display text-sm font-black tracking-tight text-white"
                                    data-testid="topic-detail-number"
                                >
                                    Topic {topic.number}
                                </span>
                                <span className="flex items-center gap-2 text-ink/55">
                                    <Clock className="h-3.5 w-3.5" />
                                    5-min read
                                </span>
                                <span className="text-ink/55">The Simple Digital Playbook</span>
                            </div>

                            <h1
                                className="font-display text-4xl font-black leading-[1] tracking-tight text-ink sm:text-5xl lg:text-[5.5rem]"
                                data-testid="topic-detail-question"
                            >
                                {topic.question}
                            </h1>

                            {drafted ? (
                                <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 lg:text-lg">
                                    A one-minute answer, a plain-English fix, and one thing you
                                    can do today — pick how you&apos;d like to take it in below.
                                </p>
                            ) : (
                                <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 lg:text-lg">
                                    This guide is being written. Every playbook guide answers one
                                    question in under five minutes and ends with{" "}
                                    <strong className="text-ink">one practical action</strong> you
                                    can complete today. Pop back soon.
                                </p>
                            )}
                        </Reveal>
                    </div>
                </section>

                {drafted ? (
                    <>
                        {/* View switcher */}
                        <section
                            className="sticky top-16 z-40 border-b border-ink/10 bg-page/90 py-4 backdrop-blur-md lg:top-20"
                            data-testid="topic-view-switcher"
                        >
                            <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-4 px-5 lg:px-10">
                                <div className="hidden text-[11px] font-bold uppercase tracking-[0.3em] text-ink/55 lg:block">
                                    Three ways to learn
                                </div>
                                <div className="flex flex-1 gap-2 lg:flex-none">
                                    {VIEWS.map((v) => {
                                        const Icon = v.icon;
                                        const active = view === v.id;
                                        return (
                                            <button
                                                key={v.id}
                                                onClick={() => setView(v.id)}
                                                className={`flex flex-1 items-center justify-center gap-2 border-2 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-all lg:flex-none ${
                                                    active
                                                        ? "border-coral bg-coral text-white"
                                                        : "border-ink/15 bg-white text-ink hover:border-coral hover:text-coral"
                                                }`}
                                                data-testid={v.testid}
                                                aria-pressed={active}
                                            >
                                                <Icon className="h-3.5 w-3.5" />
                                                {v.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>

                        {/* View bodies */}
                        {view === "read" && <ReadView topic={topic} next={next} />}
                        {view === "see" && <SeeView topic={topic} />}
                        {view === "do" && <DoView topic={topic} />}
                    </>
                ) : (
                    <DraftPlaceholder />
                )}

                {/* Prev / Next */}
                <section
                    className="border-b border-ink/10 bg-page py-14 lg:py-20"
                    data-testid="topic-detail-nav"
                >
                    <div className="mx-auto grid max-w-[1100px] gap-4 px-5 lg:grid-cols-2 lg:gap-6 lg:px-10">
                        <NeighbourCard side="prev" topic={prev} />
                        <NeighbourCard side="next" topic={next} />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

/* ---------------- READ view (full 8-section guide) ---------------- */
function ReadView({ topic, next }) {
    return (
        <section
            className="border-b border-ink/10 bg-white py-16 lg:py-24"
            data-testid="topic-read-view"
        >
            <div className="mx-auto max-w-[820px] px-5 lg:px-10">
                <Section eyebrow="The short answer" testid="section-short-answer">
                    <p className="font-display text-2xl leading-snug tracking-tight text-ink lg:text-3xl">
                        {topic.shortAnswer}
                    </p>
                </Section>

                <Section eyebrow="Why this matters" testid="section-why-matters">
                    <p className="text-base leading-relaxed text-ink/80 lg:text-lg">
                        {topic.whyThisMatters}
                    </p>
                </Section>

                {/* ☕ Over a Coffee — signature block */}
                <div
                    className="my-14 border-2 border-ink/10 bg-page p-8 lg:my-20 lg:p-12"
                    data-testid="section-over-coffee"
                >
                    <div className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                        <Coffee className="h-4 w-4" />
                        Over a Coffee
                    </div>
                    <p className="font-serif text-xl italic leading-[1.5] text-ink lg:text-2xl">
                        {topic.overCoffee}
                    </p>
                    <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-ink/45">
                        — One minute · one story
                    </div>
                </div>

                <Section eyebrow="Let's fix it" testid="section-lets-fix-it">
                    <div className="space-y-8">
                        {topic.letsFixIt.map((step, i) => (
                            <div key={i}>
                                <h3 className="font-display text-xl font-black leading-tight tracking-tight text-ink lg:text-2xl">
                                    {step.heading}
                                </h3>
                                <p className="mt-2 text-base leading-relaxed text-ink/80 lg:text-lg">
                                    {step.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* Do This Today — coral action block */}
                <div
                    className="my-14 border-2 border-coral bg-coral p-8 text-white lg:my-20 lg:p-12"
                    data-testid="section-do-this-today"
                >
                    <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-white/85">
                        <CheckCircle2 className="h-4 w-4" />
                        Do this today
                        {topic.doThisToday?.timebox ? (
                            <span className="ml-3 border border-white/40 px-2 py-0.5 text-[10px]">
                                {topic.doThisToday.timebox}
                            </span>
                        ) : null}
                    </div>
                    <h3 className="font-display text-3xl font-black leading-[1.05] tracking-tight lg:text-4xl">
                        {topic.doThisToday.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-white/90 lg:text-lg">
                        {topic.doThisToday.body}
                    </p>
                </div>

                <Section eyebrow="Simple summary" testid="section-simple-summary">
                    <ul className="space-y-3 text-base leading-relaxed text-ink/80 lg:text-lg">
                        {topic.simpleSummary.map((s, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-coral" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </Section>

                <Section eyebrow="What's next?" testid="section-whats-next">
                    <p className="text-base leading-relaxed text-ink/80 lg:text-lg">
                        {topic.whatsNext?.teaser}
                    </p>
                    {next ? (
                        <Link
                            to={`/where-should-i-start/${next.slug}`}
                            className="group mt-6 inline-flex items-center gap-3 border-2 border-ink px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:border-coral hover:text-coral"
                            data-testid="section-whats-next-cta"
                        >
                            Topic {next.number} — {next.question}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    ) : null}
                </Section>
            </div>
        </section>
    );
}

function Section({ eyebrow, testid, children }) {
    return (
        <div className="mb-12 lg:mb-16" data-testid={testid}>
            <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                {eyebrow}
            </div>
            {children}
        </div>
    );
}

/* ---------------- SEE view (typographic flowchart) ---------------- */
function SeeView({ topic }) {
    return (
        <section
            className="border-b border-ink/10 bg-white py-16 lg:py-24"
            data-testid="topic-see-view"
        >
            <div className="mx-auto max-w-[720px] px-5 text-center lg:px-10">
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                    // The flow
                </div>
                <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-ink lg:text-5xl">
                    How it works, at a glance.
                </h2>
                <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-ink/70 lg:text-base">
                    No jargon. No paragraphs. Just the journey — from someone hearing about you
                    to becoming a customer.
                </p>

                <div className="mt-14 flex flex-col items-center gap-4 lg:gap-6">
                    {topic.flowchart.map((step, i) => (
                        <React.Fragment key={i}>
                            <div
                                className="w-full max-w-md border-2 border-ink bg-page px-6 py-5 font-display text-lg font-black leading-tight text-ink lg:text-xl"
                                data-testid={`flowchart-step-${i + 1}`}
                            >
                                {step}
                            </div>
                            {i < topic.flowchart.length - 1 ? (
                                <div
                                    aria-hidden
                                    className="font-display text-3xl font-black text-coral lg:text-4xl"
                                >
                                    ↓
                                </div>
                            ) : null}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------- DO view (interactive checklist, persisted) ---------------- */
function DoView({ topic }) {
    const storageKey = `sd_topic_checklist_${topic.slug}`;
    const [checked, setChecked] = useState(() => {
        try {
            const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(checked));
        } catch (_) {}
    }, [checked, storageKey]);

    const total = topic.checklist.length;
    const done = topic.checklist.filter((_, i) => checked[i]).length;

    function toggle(i) {
        setChecked((c) => ({ ...c, [i]: !c[i] }));
    }

    function reset() {
        setChecked({});
    }

    return (
        <section
            className="border-b border-ink/10 bg-white py-16 lg:py-24"
            data-testid="topic-do-view"
        >
            <div className="mx-auto max-w-[720px] px-5 lg:px-10">
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                    // Your checklist
                </div>
                <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-ink lg:text-5xl">
                    Tick them off, one by one.
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-ink/70 lg:text-base">
                    Your ticks save on this device — come back any time to pick up where you
                    left off.
                </p>

                {/* Progress */}
                <div className="mt-8 flex items-center justify-between">
                    <div
                        className="text-[11px] font-bold uppercase tracking-[0.3em] text-ink/60"
                        data-testid="checklist-progress"
                    >
                        {done} of {total} done
                    </div>
                    {done > 0 ? (
                        <button
                            onClick={reset}
                            className="text-[11px] font-bold uppercase tracking-[0.22em] text-ink/45 hover:text-coral"
                            data-testid="checklist-reset"
                        >
                            Reset
                        </button>
                    ) : null}
                </div>
                <div className="mt-2 h-1 w-full bg-ink/10">
                    <div
                        className="h-full bg-coral transition-all"
                        style={{ width: `${total ? (done / total) * 100 : 0}%` }}
                    />
                </div>

                {/* Items */}
                <ul className="mt-10 space-y-3">
                    {topic.checklist.map((item, i) => {
                        const isDone = !!checked[i];
                        return (
                            <li key={i}>
                                <button
                                    onClick={() => toggle(i)}
                                    className={`group flex w-full items-start gap-4 border-2 p-5 text-left transition-all ${
                                        isDone
                                            ? "border-coral bg-coral/5"
                                            : "border-ink/15 bg-white hover:border-coral"
                                    }`}
                                    data-testid={`checklist-item-${i + 1}`}
                                    aria-pressed={isDone}
                                >
                                    <span className="mt-0.5 shrink-0">
                                        {isDone ? (
                                            <CheckSquare className="h-6 w-6 text-coral" />
                                        ) : (
                                            <Square className="h-6 w-6 text-ink/35 group-hover:text-coral" />
                                        )}
                                    </span>
                                    <span
                                        className={`font-display text-base font-bold leading-tight tracking-tight lg:text-lg ${
                                            isDone ? "text-ink/45 line-through" : "text-ink"
                                        }`}
                                    >
                                        {item}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {done === total && total > 0 ? (
                    <div
                        className="mt-10 border-2 border-coral bg-coral p-6 text-center text-white lg:p-8"
                        data-testid="checklist-complete"
                    >
                        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/85">
                            All done
                        </div>
                        <div className="mt-2 font-display text-2xl font-black leading-tight lg:text-3xl">
                            Nice work — that&apos;s a real thing off the list.
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
}

/* ---------------- DRAFT placeholder (topics not yet written) ---------------- */
function DraftPlaceholder() {
    return (
        <section
            className="border-b border-ink/10 bg-white py-16 lg:py-24"
            data-testid="topic-draft-placeholder"
        >
            <div className="mx-auto max-w-[820px] px-5 text-center lg:px-10">
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                    // Being written
                </div>
                <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-ink lg:text-5xl">
                    We&apos;re writing this one right now.
                </h2>
                <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-ink/70 lg:text-lg">
                    Every playbook guide gets the same care — a plain-English answer, a
                    one-minute coffee story, a fix you can start on today, and a checklist you
                    can tick through. Check back soon, or grab the free 7-day checklist below
                    to get moving today.
                </p>
                <Link
                    to="/#playbook"
                    className="mt-8 inline-flex items-center gap-2 border-2 border-ink bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:border-coral hover:text-coral"
                    data-testid="topic-draft-checklist-cta"
                >
                    Get the 7-day checklist
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </section>
    );
}

/* ---------------- Prev / Next neighbour cards ---------------- */
function NeighbourCard({ side, topic }) {
    const isPrev = side === "prev";
    const testId = isPrev ? "topic-nav-prev" : "topic-nav-next";
    if (!topic) {
        return (
            <div
                className="flex items-center justify-between border-2 border-ink/10 bg-white p-6 text-ink/35 lg:p-8"
                data-testid={testId}
            >
                <div className="text-[11px] font-bold uppercase tracking-[0.3em]">
                    {isPrev ? "First topic in the playbook" : "Last topic in the playbook"}
                </div>
            </div>
        );
    }
    return (
        <Link
            to={`/where-should-i-start/${topic.slug}`}
            className={`group flex items-center gap-4 border-2 border-ink/15 bg-white p-6 transition-all hover:-translate-y-1 hover:border-coral lg:p-8 ${
                isPrev ? "" : "lg:flex-row-reverse lg:text-right"
            }`}
            data-testid={testId}
        >
            <div className="grid h-11 w-11 shrink-0 place-items-center border-2 border-coral bg-coral text-white">
                {isPrev ? (
                    <ArrowLeft className="h-5 w-5" />
                ) : (
                    <ArrowRight className="h-5 w-5" />
                )}
            </div>
            <div className={isPrev ? "" : "lg:text-right"}>
                <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                    {isPrev ? `Previous · Topic ${topic.number}` : `Next · Topic ${topic.number}`}
                </div>
                <div className="mt-2 font-display text-lg font-black leading-[1.15] tracking-tight text-ink lg:text-xl">
                    {topic.question}
                </div>
            </div>
        </Link>
    );
}
